import { useCallback, useEffect, useRef, useState } from "react";
import { AppBar, Badge, Box, Button, Container, IconButton, Link, Stack, Toolbar, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Link as RouterLink, Route, Routes, useLocation, useNavigate } from "react-router";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import Health from "./pages/Health.jsx";
import RequestForm from "./pages/RequestForm.jsx";
import Kayttoehdot from "./pages/Kayttoehdot.jsx";
import Tietosuoja from "./pages/Tietosuoja.jsx";
import Yhteystiedot from "./pages/Yhteystiedot.jsx";
import LinkedInCallback from "src/pages/LinkedInCallback";
import Laskuri from "src/pages/Laskuri";
import MyValut from "./pages/MyValut.jsx";
import ValuDetail from "./pages/ValuDetail.jsx";
import Hallinta from "src/pages/Hallinta";
import Tuki from "src/pages/Tuki";
import LiityJerry from "src/pages/LiityJerry";
import Tarjouspyynnot from "./pages/Tarjouspyynnot.jsx";
import TarjouspyyntoDetail from "./pages/TarjouspyyntoDetail.jsx";
import TarjouspyyntoEsikatselu from "./pages/TarjouspyyntoEsikatselu.jsx";
import NotFound from "./pages/NotFound.jsx";
import RequireAuth from "src/components/valut/RequireAuth";
import RequireOperator from "src/components/RequireOperator";
import HomeResolver from "src/components/HomeResolver";
import AccountMenu from "src/components/AccountMenu";
import LanguageSwitcher from "src/components/LanguageSwitcher";
import { NavSignalsProvider, useNavSignals } from "src/hooks/useNavSignals";
import { primaryActionFor } from "src/utils/nav/navState";
import { useT } from "src/i18n";
import { useAuth } from "src/context/AuthContext";
import { AuthModalContext } from "src/context/AuthModalContext";
import AuthModal from "src/components/AuthModal";
import Wordmark from "src/components/Wordmark";
import ImpersonationBanner from "src/components/ImpersonationBanner";
import { completeMicrosoftRedirectLogin } from "src/auth/msal";
import { reaffirmAcceptances } from "src/data/legalDocument";
import { captureError } from "src/utils/errorReporting";
import { trackPageview } from "src/analytics/gtag";
import CookieConsent from "src/components/consent/CookieConsent";
import { useCookieConsent } from "src/hooks/useCookieConsent";
import { usePresenceHeartbeat } from "src/hooks/usePresenceHeartbeat";

// Operator-only app-bar action: "Tarjouspyynnöt" text button with an avoimet
// badge, collapsing to an icon button on phones. Anon/customer no longer get an
// app-bar CTA — the hero and in-page buttons cover "Pyydä tarjous".
function PrimaryAction({ isMobile, fg, action, badgeCount = 0 }) {
    const baseIcon = <RequestQuoteIcon fontSize="small" />;
    const icon = badgeCount > 0
        ? <Badge badgeContent={badgeCount} color="error" max={99}>{baseIcon}</Badge>
        : baseIcon;

    if (isMobile) {
        return (
            <Tooltip title={action.label}>
                <IconButton size="small" aria-label={action.label} component={RouterLink} to={action.to} sx={{ color: fg }}>
                    {icon}
                </IconButton>
            </Tooltip>
        );
    }
    return (
        <Button size="small" variant="text" component={RouterLink} to={action.to} startIcon={icon} sx={{ color: fg }}>
            {action.label}
        </Button>
    );
}

// Footer links share one look; only the shape varies. Pick it per target:
// a React route takes `component={RouterLink} to=`, a public/*.html page must
// take plain `href=` (a client-side nav there falls through to the "*"
// NotFound route), and the cookie-settings entry is a `component="button"`.
// `sx` is merged rather than spread over, so a caller adding its own style
// doesn't silently drop the shared colour.
function FooterLink({ sx, ...props }) {
    return <Link variant="caption" underline="hover" {...props} sx={{ color: "text.secondary", ...sx }} />;
}

function HeaderActions({ onOpenLogin }) {
    const { isAuthenticated } = useAuth();
    const { role, providerCounts, operatorNeedsActivation } = useNavSignals();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const fg = theme.custom?.appBar?.fg;
    const t = useT();
    const primary = primaryActionFor(role, { operatorNeedsActivation, t });
    const badgeCount = primary.badge ? Number(providerCounts?.avoimet) || 0 : 0;

    if (!isAuthenticated) {
        return (
            <Button
                size="medium"
                variant="outlined"
                onClick={onOpenLogin}
                sx={{
                    color: fg,
                    borderColor: fg,
                    "&:hover": { borderColor: fg, backgroundColor: "action.hover" },
                }}
            >
                {t("auth.shared.loginButton")}
            </Button>
        );
    }

    return (
        <Stack direction="row" spacing={isMobile ? 0.5 : 1} sx={{ alignItems: "center" }}>
            {!primary.cta && (
                <PrimaryAction isMobile={isMobile} fg={fg} action={primary} badgeCount={badgeCount} />
            )}
            <AccountMenu fg={fg} />
        </Stack>
    );
}

// Listens for FCM_NOTIFICATION_CLICKED postMessages from the service worker
// (sent when the user clicks an OS push notification) and navigates the focused
// tab to the target URL via react-router. The SW intentionally does not call
// existing.navigate() because click_action may be a cross-origin URL during the
// betonijerry.fi DNS transition; SPA-internal navigation is origin-agnostic.
function useFcmClickNavigation() {
    const navigate = useNavigate();
    useEffect(() => {
        if (typeof navigator === "undefined" || !navigator.serviceWorker) return undefined;
        const onMessage = (event) => {
            const msg = event.data;
            if (!msg || msg.type !== "FCM_NOTIFICATION_CLICKED") return;
            const raw = msg.url || msg.data?.url || msg.data?.click_action;
            if (!raw) return;
            try {
                // Accept absolute or relative URLs; navigate via the path portion so we
                // stay on whichever origin the user is actually on.
                const parsed = new URL(raw, window.location.origin);
                navigate(`${parsed.pathname}${parsed.search}${parsed.hash}`);
            } catch {
                // Malformed URL — ignore.
            }
        };
        navigator.serviceWorker.addEventListener("message", onMessage);
        return () => navigator.serviceWorker.removeEventListener("message", onMessage);
    }, [navigate]);
}

// Emit a GA4 page_view on every SPA route change (no-op unless analytics is on).
function usePageviews() {
    const location = useLocation();
    useEffect(() => {
        trackPageview(location.pathname + location.search);
    }, [location.pathname, location.search]);
}

function MicrosoftRedirectHandler() {
    const { loginWithMicrosoft } = useAuth();

    useEffect(() => {
        completeMicrosoftRedirectLogin({ loginWithMicrosoft, reaffirmAcceptances })
            .catch((error) => {
                captureError(error, { tags: { auth: "microsoft", operation: "redirect-complete" } });
            });
    }, [loginWithMicrosoft]);

    return null;
}

export default function App() {
    useFcmClickNavigation();
    usePageviews();
    usePresenceHeartbeat();
    const t = useT();
    // Single shared consent instance: the banner and the footer
    // "Evästeasetukset" link must see the same state (GDPR art. 7(3) —
    // withdrawing/changing consent must be as easy as giving it).
    const cookieConsent = useCookieConsent();
    const [modalOpen, setModalOpen] = useState(false);
    const onSuccessRef = useRef(null);
    const { token: authToken, isAuthenticated } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const pageTexture = theme.custom?.pageTexture || "none";
    const pageTextureSize = theme.custom?.pageTextureSize || "400px 400px";
    const pageTextureBlend = theme.custom?.pageTextureBlend || "normal";
    const pageTextureOpacity = theme.custom?.pageTextureOpacity ?? 1;
    const appBarBg = theme.custom?.appBar?.bg || theme.palette.primary.dark;
    const appBarFg = theme.custom?.appBar?.fg || theme.palette.primary.contrastText;

    const openAuthModal = useCallback(({ onSuccess } = {}) => {
        onSuccessRef.current = onSuccess || null;
        setModalOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setModalOpen(false);
        onSuccessRef.current = null;
    }, []);

    const handleSuccess = useCallback(() => {
        const cb = onSuccessRef.current;
        onSuccessRef.current = null;
        setModalOpen(false);
        if (cb) cb();
    }, []);

    return (
        <AuthModalContext.Provider value={{ openAuthModal }}>
            <NavSignalsProvider>
            <MicrosoftRedirectHandler />
            <Box
                sx={(theme) => ({
                    minHeight: "100vh",
                    bgcolor: "background.default",
                    position: "relative",
                    "&::before": pageTexture === "none" ? {} : {
                        content: '""',
                        position: "fixed",
                        inset: 0,
                        backgroundImage: pageTexture,
                        backgroundSize: pageTextureSize,
                        backgroundRepeat: "repeat",
                        mixBlendMode: pageTextureBlend,
                        opacity: pageTextureOpacity,
                        pointerEvents: "none",
                        zIndex: 0,
                    },
                    "& > *": { position: "relative", zIndex: 1 },
                    // Skip-to-content link: first focusable element, off-screen until
                    // focused. The `.bj-skip-link` selector (0,2,0) outranks `& > *`
                    // (0,1,0) so its fixed position + z-index survive the reset above.
                    "& > .bj-skip-link": {
                        position: "fixed",
                        top: -64,
                        left: 8,
                        zIndex: theme.zIndex.tooltip + 1,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: "warning.main",
                        color: "warning.contrastText",
                        fontSize: 14,
                        fontWeight: 700,
                        textDecoration: "none",
                        transition: "top 140ms ease-out",
                        "&:focus, &:focus-visible": {
                            top: 8,
                            outline: "3px solid #FFD23F",
                            outlineOffset: 2,
                        },
                        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                    },
                })}
            >
                <Box component="a" href="#main-content" className="bj-skip-link">
                    {t("common.skipToContent")}
                </Box>
                <ImpersonationBanner />
                <AppBar position="sticky" sx={{ top: 0, zIndex: (t) => t.zIndex.appBar, bgcolor: appBarBg, color: appBarFg }}>
                    <Toolbar sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Wordmark size={isMobile ? "h6" : "h5"} to="/" state={{ stay: true }} />
                        </Box>
                        <Stack
                            component="nav"
                            aria-label="Päänavigaatio"
                            direction="row"
                            spacing={2.5}
                            sx={{
                                alignItems: "center",
                                justifyContent: { xs: "center", md: "flex-start" },
                                mr: { xs: 0, md: 2.5 },
                                pb: { xs: 0.5, md: 0 },
                                // Single set of anchors, never a second JS/CSS-hidden copy: a
                                // duplicate <a href> to the same target is an internal-linking
                                // smell a crawler would flag. Below md the Toolbar wraps (see
                                // flexWrap above) and `order` pushes this Stack onto its own
                                // full-width second row *without* disturbing the Wordmark/
                                // HeaderActions row above it — `flexBasis: 100%` is what forces
                                // the wrap. At md+, order/flexBasis revert to the natural
                                // Wordmark–nav–HeaderActions inline row. Either way the same two
                                // anchors stay in the rendered DOM at every viewport (not
                                // JS-conditionally unmounted) so a narrow-viewport crawl still
                                // discovers them — the entire point of this nav. Breakpoint is
                                // md (900px), not sm (600px): measured Toolbar content (padding +
                                // wordmark + nav + login button ≈ 597px) leaves no safe margin
                                // against the browser scrollbar right at a 600px viewport, so an
                                // inline row would clip at that boundary — hence wrapping to a
                                // second row instead of trying to fit inline that early.
                                flexBasis: { xs: "100%", md: "auto" },
                                order: { xs: 3, md: 0 },
                            }}
                        >
                            {/* Plain anchor (href), never RouterLink `to=` like the items in
                                HeaderActions/AccountMenu below: the target is a static HTML
                                page generated into dist/ at build time, outside the React
                                router. A client-side route push to that path would match no
                                <Route> and land on the SPA fallback (NotFound) instead of the
                                real page — do not "harmonise" this into a RouterLink. */}
                            <Link
                                href="/betonipumppaus-toimitusalueet"
                                variant="body2"
                                underline="hover"
                                sx={{ color: appBarFg, fontWeight: 600, whiteSpace: "nowrap" }}
                            >
                                {t("common.nav.coverageAreas")}
                            </Link>
                        </Stack>
                        <LanguageSwitcher />
                        <HeaderActions key={authToken || "anon"} onOpenLogin={() => openAuthModal()} />
                    </Toolbar>
                </AppBar>
                <Container
                    component="main"
                    id="main-content"
                    tabIndex={-1}
                    maxWidth="lg"
                    sx={{ py: { xs: 3, md: 4 }, outline: "none" }}
                >
                    <Routes>
                        <Route path="/" element={<HomeResolver />} />
                        <Route path="/tilaa" element={<RequestForm />} />
                        <Route path="/laskuri" element={<Laskuri />} />
                        <Route path="/kayttoehdot" element={<Kayttoehdot />} />
                        <Route path="/tietosuoja" element={<Tietosuoja />} />
                        <Route path="/yhteystiedot" element={<Yhteystiedot />} />
                        <Route path="/health" element={<Health />} />
                        <Route path="/oauth/linkedin/callback" element={<LinkedInCallback />} />
                        <Route path="/valut" element={<RequireAuth><MyValut /></RequireAuth>} />
                        <Route path="/valut/:id" element={<RequireAuth><ValuDetail /></RequireAuth>} />
                        {/* PUBLIC tokenized preview (email CTA landing) — must stay ungated. */}
                        <Route path="/tarjouspyynnot/esikatselu" element={<TarjouspyyntoEsikatselu />} />
                        {/* Operator-only: RequireOperator bounces a customer session to its own
                            home, so an identity swap (impersonate, company switch) can't strand
                            one on a provider route. */}
                        <Route path="/tarjouspyynnot" element={<RequireAuth><RequireOperator><Tarjouspyynnot /></RequireOperator></RequireAuth>} />
                        <Route path="/tarjouspyynnot/:id" element={<RequireAuth><RequireOperator><TarjouspyyntoDetail /></RequireOperator></RequireAuth>} />
                        <Route path="/hallinta" element={<RequireAuth><RequireOperator><Hallinta /></RequireOperator></RequireAuth>} />
                        <Route path="/tuki" element={<RequireAuth><RequireOperator><Tuki /></RequireOperator></RequireAuth>} />
                        <Route path="/liity" element={<RequireAuth><RequireOperator><LiityJerry /></RequireOperator></RequireAuth>} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Container>
                <Box
                    component="footer"
                    sx={{
                        py: 1.5,
                        borderTop: 1,
                        borderColor: "divider",
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={2.5}
                        sx={{
                            justifyContent: "center",
                            alignItems: "center",
                            flexWrap: "wrap",
                            rowGap: 0.5,
                            // Raise the footer links' hit area toward the 44px touch-target
                            // floor without growing the caption text itself.
                            "& .MuiLink-root": { display: "inline-block", py: 1 },
                        }}
                    >
                        {isAuthenticated && (
                            <FooterLink component={RouterLink} to="/kayttoehdot">
                                {t("common.footer.terms")}
                            </FooterLink>
                        )}
                        <FooterLink component={RouterLink} to="/laskuri">
                            {t("common.footer.calculator")}
                        </FooterLink>
                        <FooterLink href="/betonipumppauksen-hinta">
                            {t("common.footer.pumpingPrice")}
                        </FooterLink>
                        <FooterLink href="/betonipumppaus-toimitusalueet">
                            {t("common.footer.coverageAreas")}
                        </FooterLink>
                        <FooterLink href="https://peli.betoni.online" target="_blank" rel="noopener">
                            {t("common.footer.game")}
                        </FooterLink>
                        <FooterLink component={RouterLink} to="/tietosuoja">
                            {t("common.footer.privacy")}
                        </FooterLink>
                        {cookieConsent.available && (
                            <FooterLink component="button" type="button" onClick={cookieConsent.reopen}>
                                {t("common.footer.cookieSettings")}
                            </FooterLink>
                        )}
                        <FooterLink component={RouterLink} to="/yhteystiedot">
                            {t("common.footer.contact")}
                        </FooterLink>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            betonijerry v{__APP_VERSION__}
                        </Typography>
                    </Stack>
                </Box>
                <CookieConsent consent={cookieConsent} />
                <AuthModal
                    open={modalOpen}
                    onClose={handleClose}
                    onSuccess={handleSuccess}
                />
            </Box>
            </NavSignalsProvider>
        </AuthModalContext.Provider>
    );
}
